import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Template} from "../../../../../../components/template/template.jsx";
import {Card} from "../../../../../../components/card/card.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../../utils/aggrid-utils.js";
import {navigateHook} from "../../../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import sosService from "../../../../../../services/web/sos-service.js";
import projectUtils from "../../../../../../utils/project-utils.js";
import InputForm from "../../../../../../components/input/input-form.jsx";
import SelectComponent from "../../../../../../components/select/select-component.jsx";
import InputDatetime from "../../../../../../components/input/input-datetime.jsx";
import Button from "../../../../../../components/button/button.jsx";
import {FaEraser, FaSearch} from "react-icons/fa";
import protectionProgramService from "../../../../../../services/web/protection-program-service.js";

const initialFilters = {
    name: "",
    protectionProgram: [],
    dateStart: "",
    dateEnd: "",
}

const SosHistoryList = () => {
    const navigate = navigateHook();

    const [filter, setFilter] = useState({filter:[],page:1,size:0,sort:[{field:"name",dir:"asc"}]});
    const [filterValues, setFilterValues] = useState(initialFilters);

    const {data} = useQuery({
        queryFn: () => sosService.listHistory(filter),
        queryKey: ["list", {filter}],
    })

    const {data: programs} = useQuery({
        queryFn: () => protectionProgramService.options(),
        queryKey: ["program"]
    });

    const gridRef = useRef(undefined);

    const [columnDefs, ] = useState([
        {headerName: "ID", field: "id", sort: "desc"},
        {headerName: "Nome/Razão Social", field: "citizen.name"},
        {headerName: "Programa de proteção", field: "citizen.protectionPrograms", valueFormatter: ({value}) => {
                const batata = value.split(",");
                const newResult = new Set();
                batata.forEach(b => newResult.add(b.trim()))

                return [...newResult].join(", ")
            }},
        {headerName: "Acionamento", field: "dtStart", comparator: aggridUtils.agGridCompareDateTime},
        {headerName: "Assumido", field: "dtAck", comparator: aggridUtils.agGridCompareDateTime},
        {headerName: "Encerrado", field: "dtEnd", comparator: aggridUtils.agGridCompareDateTime},
        {headerName: "Tempo para assumir", field: "delta",
            valueFormatter: ({value}) => {
                if (value) {
                    const totalMinutes = Math.floor(value / 60);

                    const seconds = value % 60;
                    const hours = Math.floor(totalMinutes / 60);
                    const minutes = totalMinutes % 60;

                    let timeStr = '';
                    if (hours) timeStr += hours + 'h';

                    if (minutes) {
                        if (timeStr !== '') timeStr += ', ';
                        timeStr += minutes + 'min';
                    }

                    if (seconds) {
                        if (timeStr !== '') timeStr += ' e ';
                        timeStr += seconds + 's';
                    }

                    return timeStr;
                }
            }
        },
        {headerName: "Telefone", field: "citizen.phone", valueFormatter: ({value}) => projectUtils.formatPhone(value)},
        {headerName: "Providência", field: "providence", valueFormatter: ({value}) => projectUtils.formatPhone(value)}
    ]);

    const defaultColDef = useMemo(() => ({flex: 1, comparator: aggridUtils.nullLastComparator}), []);

    const theme = useMemo(() => {
        return aggridUtils.generalTheme();
    }, []);

    const onCellClicked = ({event, data}) => {
        const isBlank = event.ctrlKey;
        navigate(`/sos/event-history/${data.id}`, isBlank);
    }

    const handleInput = ({target: {id, value}}) => setFilterValues(prev => ({...prev, [id]: value}));

    const prepareFilter = (filter) => {
        return [
            {
                "field": "name",
                "type": "like",
                "value": filter.name
            },
            {
                "field": "protectionProgram",
                "type": "like",
                "value": filter.protectionProgram?.map(p => p.value).join(",") ?? ""
            },
            {
                "field": "dtStart",
                "type": "keywords",
                "value": {
                    "dateStart": filter.dateStart,
                    "dateEnd": filter.dateEnd
                }
            }
        ]
    }

    const apply = useCallback(() => {
        setFilter(prev =>
            ({
                ...prev,
                filter: prepareFilter(filterValues)
            })
        )
    }, [filterValues])

    const clear = useCallback(() => {
        setFilterValues(initialFilters)
        setFilter({
            filter: prepareFilter(initialFilters),
            page:1,
            size:500,
            sort:[{field:"name",dir:"asc"}]
        });
    }, [])

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Enter") {
                apply();
            }
            if (event.key === "Escape") {
                clear();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [apply, clear]);

    return (
        <Template>
            <Template.Header>
                <Template.Title>Histórico de SOS</Template.Title>
            </Template.Header>

            <Template.Body className={"space-y-2"}>
                <Card>
                    <Card.Body className={"grid grid-cols-12 items-stretch gap-4"}>
                        <InputForm
                            classNameWrapper={"col-span-4"}
                            id={"name"}
                            label={"Nome/Razão Social"}
                            value={filterValues.name}
                            onChange={handleInput}
                        />

                        <SelectComponent
                            classNameWrapper={"col-span-3"}
                            value={filterValues.protectionProgram}
                            options={programs ?? []}
                            label={"Programa"}
                            isMulti
                            onChange={(value) => setFilterValues(prev => ({...prev, protectionProgram: value}))}
                        />

                        <div className={"col-span-5 flex items-stretch"}>
                            <InputDatetime
                                id="dateStart"
                                label="Acionamento entre"
                                selected={filterValues.dateStart}
                                onChange={(date) => setFilterValues(prev => ({...prev, dateStart: date }))}
                                dateFormat="L"
                                customIcon
                                returnDateObj={false}
                                inputClass="!rounded-e-none"
                                classNameWrapper="w-[47%]"
                            />

                            <div className="border border-[#E5E7EB] px-4 py-2 text-sm leading-none">à</div>

                            <InputDatetime
                                id="dtEnd"
                                selected={filterValues.dtEnd}
                                onChange={(date) => setFilterValues(prev => ({...prev, dateEnd: date }))}
                                dateFormat="L"
                                returnDateObj={false}
                                inputClass="!rounded-s-none"
                                classNameWrapper="w-[47%]"
                            />

                            <div className={"flex gap-2 justify-end pl-3"}>
                                <Button target={"default"} onClick={clear}>
                                    <FaEraser />
                                </Button>
                                <Button onClick={apply}>
                                    <FaSearch />
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body>
                        <div className={"h-[calc(100vh-153px)] aggrid-row-pointer"}>
                            <AgGridReact
                                ref={gridRef}
                                rowHeight={35}
                                rowData={data ?? []}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                animateRows={false}
                                suppressCellFocus={true}
                                onCellClicked={onCellClicked}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                pagination
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                theme={theme}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default SosHistoryList;
